import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { logEvent } from '~/config/firebase.config';
import { IClient, Mode, ClientDatabase } from '~/types/client';
import { useGames } from '~/hooks/models/useGame';
import { useFullscreen } from '~/hooks/useFullscreen';
import { GAMES, compareGamesCatalogOrder } from '~/config/global.config';
import { SEASONAL_GAMES, SeasonalThemeId } from '~/config/seasonalTheme.config';

import { LoadingPage } from '~/pages/LoadingPage';
import { HomeDemoModal } from '~/components/modal/HomeDemoModal';
import { HomeUnsubscribedModal } from '~/components/modal/HomeUnsubscribedModal';
import { HomePausedModal } from '~/components/modal/HomePausedModal';
import { usePauseActive } from '~/hooks/usePauseActive';
import { useScreenTabLock } from '~/hooks/useScreenTabLock';
import { ScreenTabChoice } from '~/components/screenTab';
import { ScreenHeartbeat } from '~/components/session';
import { AnimatorPromo } from '~/components/animatorPromo';
import { MenuPlayersQR } from '~/components/menuPlayersQR/MenuPlayersQR';
import { DualScreenButton } from '~/components/dualScreen/DualScreenButton';

import styles from './HomeAnimation.redesign.module.css';

export type HomeAnimationProps = {
    client: IClient;
    /** Thème de saison actif (HomeContent) : pastilles + jeux de saison en tête. */
    seasonal?: SeasonalThemeId | null;
};

function Svg(props: any) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            {...props}
        />
    );
}
function IconPlay() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
        </svg>
    );
}
function IconPlayers() {
    return (
        <Svg>
            <path d="M17 21v-2a4 4 0 0 0-3-3.87M7 21v-2a4 4 0 0 1 3-3.87" />
            <circle cx="9" cy="7" r="4" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </Svg>
    );
}
function IconClock() {
    return (
        <Svg>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
        </Svg>
    );
}
function IconLock() {
    return (
        <Svg>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </Svg>
    );
}

export function HomeAnimation({ client, seasonal }: HomeAnimationProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFullscreen, toggleFullscreen } = useFullscreen();

    // Jeu choisi retenu par son id (pas sa position) : quand le thème de saison réordonne
    // la liste (allumage, coupure), le jeu sélectionné ne change pas sous les yeux.
    const [pickedID, setPickedID] = useState<string>();

    const gamesData = useGames(client?.gameIDs.length > 0 ? client.gameIDs : GAMES);
    // Saison : les jeux qui ont un contenu de saison passent en tête (le 1er est présélectionné).
    const seasonGames = seasonal ? SEASONAL_GAMES[seasonal] : [];
    const seasonRank = (id: string) => {
        const i = seasonGames.indexOf(id);
        return i < 0 ? seasonGames.length : i;
    };
    const isSeasonGame = (id?: string) => !!id && seasonGames.includes(id);
    const games = gamesData
        ?.slice()
        .sort((a, b) => seasonRank(a.id) - seasonRank(b.id) || compareGamesCatalogOrder(a, b));

    // Pause d'abonnement en cours : jeux coupés, calque dédié (≠ résilié/disabled).
    // Réévalué pile au début / à la fin de la pause (TV laissée sur le menu).
    const paused = usePauseActive(client.pause);

    const launchGame = (clientID: string, gameID: string) => {
        if (paused) return;
        if (gameID) {
            logEvent('home_game_click', clientID === 'demo' ? 'Demo' : 'Home', { clientID, gameID });
            if (!isFullscreen) {
                toggleFullscreen();
            }
            navigate(`/app/${clientID}?game=${gameID}`);
        }
    };

    const selectGame = (index: number) => {
        const id = games?.[index]?.id;
        if (id) setPickedID(id);
    };

    // Real (paying) anim/bar establishment: not the public demo, not churned.
    // Same scope as the presence heartbeat / AnimatorPromo below.
    // En pause : ni présence LIVE ni lancement à distance depuis le cockpit.
    const isRealClient = !client.isDemo() && !client.disabled && !paused;
    // Le menu suit le cockpit (ci-dessous) : un menu oublié dans un onglet caché
    // entrerait dans chaque jeu lancé ailleurs → veille s'il est ouvert ailleurs.
    const screenTab = useScreenTabLock(client.id, isRealClient);

    // --- Animator Control depuis le MENU (le pont manquant) -------------------
    // Jusqu'ici, l'écran n'écoutait le téléphone animateur QUE une fois en jeu
    // (ScreenApp). Sur le menu, choisir un jeu depuis le cockpit n'avait aucun
    // effet — bouton actif mais mort, source de confusion (retour Ninkasi).
    //
    // Ici on branche le menu sur le même tube Firestore : quand l'animateur
    // choisit un jeu (screenInfos.anim.gameID redevient non-null), la TV entre
    // dans le jeu — exactement comme un clic sur une carte. Le « Lancer » (tap 2)
    // est ensuite géré par ScreenApp, déjà monté. Flux : choisir → puis Lancer.
    //
    // Anti-valeur-périmée : gameID n'est jamais remis à zéro (stopScreenSession
    // désactivé), donc au montage il porte le DERNIER jeu joué. Deux mesures :
    //  1. On le remet à null (clearScreenGame) → le cockpit reflète l'état « menu »
    //     et tous les jeux redeviennent sélectionnables (fini la tuile fantôme).
    //  2. On ignore la valeur présente au montage et on ne navigue que sur un vrai
    //     CHANGEMENT vers un jeu non-null (motif seenRef/initRef, comme
    //     useRemoteLaunchSignal dans ScreenApp). Insensible à l'ordre d'arrivée des
    //     écritures Firestore, et un re-choix du même jeu (null → X) navigue quand même.
    const navRef = useRef(navigate);
    navRef.current = navigate;
    const seenRef = useRef<string | null | undefined>(undefined);
    const initRef = useRef(false);
    const remoteGameID = isRealClient ? client.getScreen(Mode.Anim)?.gameID : undefined;

    useEffect(() => {
        if (!isRealClient) return;
        // Nouvel établissement : on ré-ignore la 1re valeur (voir effet ci-dessous).
        initRef.current = false;
        ClientDatabase.clearScreenGame(client.id, Mode.Anim).catch(() => {
            /* best effort — sans reset on ignore juste la valeur périmée ci-dessous */
        });
    }, [isRealClient, client.id]);

    useEffect(() => {
        if (!isRealClient) return;
        const prev = seenRef.current;
        seenRef.current = remoteGameID;
        if (!initRef.current) {
            // 1re valeur après (re)montage : périmée → on l'adopte sans naviguer.
            initRef.current = true;
            return;
        }
        // Changement réel vers un jeu (non-null) = choix de l'animateur → on entre.
        if (remoteGameID && remoteGameID !== prev) {
            logEvent('animator_menu_launch', client.id, { clientID: client.id, gameID: remoteGameID });
            navRef.current(`/app/${client.id}?game=${remoteGameID}`);
        }
    }, [isRealClient, remoteGameID, client.id]);

    if (!games) {
        return <LoadingPage>{t('home.loadingGames')} ...</LoadingPage>;
    }

    // Démo : jamais un jeu verrouillé (au-delà du 3e) resté sélectionné après un réordonnancement.
    const current = games.find((g, i) => g.id === pickedID && !(client.isDemo() && i > 2)) ?? games[0];

    return (
        <div className={classNames(styles.screen, seasonal && styles[seasonal])}>
            {current?.video ? (
                <video key={current.video} autoPlay muted loop playsInline className={styles.backgroundVideo}>
                    <source src={current.video} type="video/mp4" />
                </video>
            ) : (
                // Pas de vidéo trailer → image de fond `cover` (évite le repli sur
                // la vidéo d'un autre jeu). `cover` retombe sur l'icône si absent.
                <img key={current?.cover} src={current?.cover} alt="" className={styles.backgroundVideo} />
            )}
            <div className={styles.scrim} />

            <div className={styles.overlay}>
                <div className={styles.badges}>
                    {seasonal && isSeasonGame(current?.id) && (
                        <span className={classNames(styles.badge, styles.badgeSeason)}>
                            <span aria-hidden="true">🎃</span>
                            {t(`home.seasonal.${seasonal}.content.${current.id}`)}
                        </span>
                    )}
                    <span className={classNames(styles.badge, styles.badgePlayers)}>
                        <IconPlayers />
                        {current?.numberOfPlayers}
                    </span>
                    <span className={classNames(styles.badge, styles.badgeTime)}>
                        <IconClock />
                        {current?.playTime}
                    </span>
                </div>

                <h1 className={styles.title}>{current?.name}</h1>

                <button type="button" className={styles.start} onClick={() => launchGame(client?.id, current?.id)}>
                    <IconPlay />
                    {t('home.start')}
                </button>

                <h4 className={styles.sectionTitle}>{t('home.allGames')}</h4>
                <small className={styles.sectionHint}>{t('home.selectGame')}</small>

                <div className={styles.rail}>
                    {games.map((game, index) => {
                        const disabled = (client.isDemo() && index > 2) || client.disabled || paused;
                        return (
                            <button
                                type="button"
                                key={game.id}
                                className={classNames(
                                    styles.gcard,
                                    game.id === current?.id && styles.gcardSel,
                                    disabled && styles.gcardDisabled
                                )}
                                onClick={() => selectGame(index)}
                                disabled={disabled}
                            >
                                <div className={styles.gthumbWrap}>
                                    <img className={styles.gthumb} src={game.icon} alt={game.name} />
                                    {isSeasonGame(game.id) && !disabled && (
                                        <span className={styles.seasonTag} aria-hidden="true">
                                            🎃
                                        </span>
                                    )}
                                    {disabled && (
                                        <span className={styles.lock}>
                                            <IconLock />
                                        </span>
                                    )}
                                </div>
                                <div className={styles.gname}>{game.name}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {client.isDemo() && <HomeDemoModal />}
            {client?.disabled && <HomeUnsubscribedModal client={client} />}
            {!client?.disabled && paused && <HomePausedModal client={client} />}

            {/* Mark the establishment LIVE as soon as the launcher (menu) is open,
                not only once a game is launched — so it shows up in the animator
                app and the remote card below deep-links to a reachable cockpit. */}
            {isRealClient && <ScreenHeartbeat clientID={client.id} />}
            {/* Coin droit : la télécommande (carte d'adoption puis badge). Plus de QR des
                joueurs en permanence sur le menu (29/10 : menu trop chargé). */}
            {isRealClient && (
                <div className={styles.rightColumn}>
                    {/* Double écran « Régie » : écran public sur le projecteur, commandes ici (Pro). */}
                    {current?.id && <DualScreenButton client={client} gameID={current.id} />}
                    <AnimatorPromo key={`promo-${client.id}`} clientID={client.id} inColumn />
                </div>
            )}
            {/* QR des joueurs en grand, seulement à la touche « QR » de la télécommande (pas de
                bouton mort sur le menu). Hors de la colonne (étage 5) : le calque passe
                au-dessus de la barre du haut au lieu d'être rogné par elle. */}
            {isRealClient && <MenuPlayersQR key={`qr-${client.id}`} client={client} />}
            <ScreenTabChoice
                others={screenTab.others}
                onTakeOver={screenTab.takeOver}
                onKeepBoth={screenTab.keepBoth}
            />
        </div>
    );
}
